import React, { useEffect, useState } from "react";
import {
  buildCompanyAliases,
  buildCompanyKpis,
  buildCompanyLinkedEntityGroups,
  buildCompanyPipelineCells,
  buildCompanyKeyPeople,
  buildCompanyRecentActivityRows,
  buildCompanySignalRows,
  buildTopProducts
} from "../utils/companyDetail";

const KPI_CONFIG = [
  { key: "patents", label: "Patents", tone: "text-amber-300" },
  { key: "trials", label: "Trials", tone: "text-emerald-300" },
  { key: "fdaProducts", label: "FDA Clearances", tone: "text-cyan-300" },
  { key: "publications", label: "Publications", tone: "text-sky-300" },
  { key: "activeProducts", label: "Active Products", tone: "text-teal-300" },
];

const toneClassBySignal = {
  info: "border-cyan-400/55 bg-cyan-500/10 text-cyan-100",
  neutral: "border-slate-600/70 bg-slate-800/45 text-slate-200",
  warning: "border-amber-400/55 bg-amber-500/10 text-amber-100",
};

const linkedEntityToneByCollection = {
  patents: "bg-amber-300",
  clinical_trials: "bg-emerald-300",
  pubmed: "bg-sky-300",
  fda_510k: "bg-cyan-300",
  de_novo: "bg-cyan-300",
  fda_denovo: "bg-cyan-300",
  fda_pma: "bg-cyan-300",
  fda_drugs: "bg-teal-300",
};

const renderSectionHeading = (label) => (
  <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">{label}</h3>
);

const getCollectionToneClass = (collection) =>
  linkedEntityToneByCollection[collection] || "bg-slate-400";

const CompanyDetailLayout = ({ record, onOpenLinkedRecord }) => {
  const [isAliasesExpanded, setIsAliasesExpanded] = useState(false);
  const [aliasQuery, setAliasQuery] = useState("");
  const [copiedAlias, setCopiedAlias] = useState("");
  const [selectedLinkedEntityDataset, setSelectedLinkedEntityDataset] = useState("");
  const [linkedEntityQuery, setLinkedEntityQuery] = useState("");

  const kpis = buildCompanyKpis(record);
  const pipelineCells = buildCompanyPipelineCells(record);
  const topProducts = buildTopProducts(record);
  const keyPeople = buildCompanyKeyPeople(record);
  const recentActivityRows = buildCompanyRecentActivityRows(record);
  const signalRows = buildCompanySignalRows(record);
  const aliases = buildCompanyAliases(record);
  const linkedEntityGroups = buildCompanyLinkedEntityGroups(record);
  const filteredAliases = aliasQuery.trim()
    ? aliases.filter((alias) => alias.toLowerCase().includes(aliasQuery.trim().toLowerCase()))
    : aliases;
  const selectedLinkedEntityGroup = linkedEntityGroups.find(
    (group) => group.datasetKey === selectedLinkedEntityDataset
  );

  const handleCopyAlias = async (alias) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(alias);
      }
      setCopiedAlias(alias);
    } catch {
      setCopiedAlias(alias);
    }
  };

  useEffect(() => {
    setSelectedLinkedEntityDataset("");
    setLinkedEntityQuery("");
  }, [record?._record_id]);

  const filteredLinkedEntities = linkedEntityQuery.trim()
    ? (selectedLinkedEntityGroup?.items || []).filter((entity) => {
        const query = linkedEntityQuery.trim().toLowerCase();

        return (
          entity.id?.toLowerCase().includes(query) ||
          entity.title?.toLowerCase().includes(query) ||
          entity.collection?.toLowerCase().includes(query) ||
          entity.datasetLabel?.toLowerCase().includes(query)
        );
      })
    : selectedLinkedEntityGroup?.items || [];

  const handleSelectLinkedEntityDataset = (datasetKey) => {
    setSelectedLinkedEntityDataset(datasetKey);
    setLinkedEntityQuery("");
  };

  const handleBackToLinkedEntityDatasets = () => {
    setSelectedLinkedEntityDataset("");
    setLinkedEntityQuery("");
  };

  return (
    <>
      <div className="space-y-6">

        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
          {KPI_CONFIG.map((item) => (
            <div
              key={`detail-kpi-${item.key}`}
              className="rounded-lg border border-slate-700/80 bg-slate-950/45 px-3 py-3 text-center"
            >
              <p className={`text-[1.55rem] font-semibold leading-none ${item.tone}`}>
                {Number(kpis[item.key] || 0)}
              </p>
              <p className="mt-2 text-[9px] uppercase tracking-[0.16em] text-slate-500">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-700/80 pt-5">
          <div className="space-y-3">
            {renderSectionHeading("Pipeline By Stage")}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,6.5rem),1fr))] gap-1.5">
              {pipelineCells.map((cell) => (
                <div
                  key={`detail-pipeline-${cell.stage}`}
                  className={`rounded-md border px-2 py-2 text-center ${
                    cell.isActive ? "border-cyan-400/70 bg-cyan-500/10" : "border-slate-700/80 bg-slate-950/45"
                  }`}
                >
                  <p className={`text-xl font-semibold leading-none ${cell.isActive ? "text-cyan-200" : "text-slate-500"}`}>
                    {cell.count}
                  </p>
                  <p className="mt-2 break-words text-[9px] uppercase tracking-[0.16em] text-slate-500">
                    {cell.stage}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
            {renderSectionHeading("Top Products")}

            {topProducts.length ? (
              <div className="mt-3 divide-y divide-slate-700/70 text-sm">
                {topProducts.map((product, index) => (
                  <div
                    key={`detail-product-${index}`}
                    className="py-2 text-slate-100"
                  >
                    {product}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500">
                No products available.
              </p>
            )}
          </div>
        
        <div className="space-y-5 border-t border-slate-700/80 pt-5">
          <div>
            {renderSectionHeading("Key People")}
            <div className="mt-3 divide-y divide-slate-700/70 text-sm">
              <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[9rem_1fr] sm:gap-3">
                <p className="text-slate-400">Top inventor</p>
                <p className="break-words text-slate-100">{keyPeople.topInventor || "Unavailable"}</p>
              </div>
              <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[9rem_1fr] sm:gap-3">
                <p className="text-slate-400">Top investigator</p>
                <p className="break-words text-slate-100">{keyPeople.topInvestigator || "Unavailable"}</p>
              </div>
            </div>
          </div>

          <div>
            {renderSectionHeading("Recent Activity")}
            {recentActivityRows.length ? (
              <div className="mt-3 divide-y divide-slate-700/70 text-sm">
                {recentActivityRows.map((row) => (
                  <div
                    key={`detail-activity-${row.id}`}
                    className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[6rem_1fr] sm:gap-3"
                  >
                    <p className="text-slate-400">{row.dateLabel}</p>
                    <p className="break-words text-slate-100">
                      {row.label}
                      {row.detail ? <span className="ml-1 text-xs text-slate-400">({row.detail})</span> : null}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500">No dated activity.</p>
            )}
          </div>

          <div>
            {renderSectionHeading("CI Intelligence")}
            {signalRows.length ? (
              <div className="mt-3 space-y-2">
                {signalRows.map((signal) => (
                  <p
                    key={`detail-signal-${signal.id}`}
                    className={`rounded-md border px-3 py-2 text-sm ${toneClassBySignal[signal.tone] || toneClassBySignal.info}`}
                  >
                    {signal.text}
                  </p>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500">No defensible intelligence signals yet.</p>
            )}
          </div>
        </div>

                <section className="space-y-3 border-t border-slate-700/80 pt-5">
          {renderSectionHeading("Aliases")}
          {aliases.length ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex max-w-full items-center rounded-full border border-slate-700 bg-slate-950/70 px-3 py-1.5 text-sm font-medium text-slate-100">
                  {aliases[0]}
                </span>
                {aliases.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => setIsAliasesExpanded((currentValue) => !currentValue)}
                    className="inline-flex items-center rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-100 transition-colors hover:border-cyan-300 hover:text-white"
                    aria-expanded={isAliasesExpanded}
                  >
                    {isAliasesExpanded ? "Hide aliases" : `+${aliases.length - 1} more`}
                  </button>
                ) : null}
              </div>

              {isAliasesExpanded ? (
                <div className="space-y-3 rounded-lg border border-slate-800/80 bg-slate-950/35 p-3">
                  <label className="block">
                    <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Search aliases
                    </span>
                    <input
                      type="search"
                      value={aliasQuery}
                      onChange={(event) => setAliasQuery(event.target.value)}
                      placeholder="Filter aliases"
                      className="w-full rounded-md border border-slate-700 bg-slate-950/80 px-3 py-2 text-sm text-slate-100 outline-none transition-all placeholder:text-slate-500 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/15"
                    />
                  </label>

                  <div className="max-h-56 overflow-y-auto pr-1">
                    <div className="space-y-2">
                      {filteredAliases.length ? (
                        filteredAliases.map((alias) => (
                          <div
                            key={alias}
                            className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2"
                          >
                            <p className="min-w-0 break-words text-sm text-slate-100">{alias}</p>
                            <button
                              type="button"
                              onClick={() => handleCopyAlias(alias)}
                              className="shrink-0 rounded-md border border-slate-700 bg-slate-950 px-2.5 py-1 text-[11px] font-semibold text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
                            >
                              {copiedAlias === alias ? "Copied" : "Copy"}
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500">No aliases match.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No aliases available.</p>
          )}
        </section>

        <section className="space-y-3 border-t border-slate-700/80 pt-5">
          {renderSectionHeading("Linked Entities")}

          {linkedEntityGroups.length ? (
            selectedLinkedEntityGroup ? (
              <div className="space-y-3 rounded-lg border border-slate-800/80 bg-slate-950/35 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={handleBackToLinkedEntityDatasets}
                    className="inline-flex items-center rounded-full border border-slate-700 bg-slate-950/80 px-3 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-white"
                  >
                    Back to datasets
                  </button>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-100">
                      {selectedLinkedEntityGroup.datasetLabel}
                    </p>
                    <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      {selectedLinkedEntityGroup.items.length} linked entities
                    </p>
                  </div>
                </div>

                <label className="block">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Search linked entities
                  </span>
                  <input
                    type="search"
                    value={linkedEntityQuery}
                    onChange={(event) => setLinkedEntityQuery(event.target.value)}
                    placeholder="Filter linked entities"
                    className="w-full rounded-md border border-slate-700 bg-slate-950/80 px-3 py-2 text-sm text-slate-100 outline-none transition-all placeholder:text-slate-500 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/15"
                  />
                </label>

                <div className="max-h-72 overflow-y-auto pr-1">
                  <div className="space-y-2">
                    {filteredLinkedEntities.length ? (
                      filteredLinkedEntities.map((entity) => (
                        <div
                          key={`${entity.datasetKey}-${entity.id}`}
                          className="flex items-start justify-between gap-3 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm text-slate-200"
                        >
                          <div className="flex min-w-0 items-start gap-3">
                            <span
                              className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${getCollectionToneClass(
                                entity.collection
                              )}`}
                            />

                            <span className="min-w-0">
                              <span className="block font-mono text-[11px] uppercase tracking-[0.1em] text-slate-400">
                                {entity.id}
                              </span>
                              <span className="mt-0.5 block break-words text-slate-100">
                                {entity.title}
                              </span>
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => onOpenLinkedRecord?.(entity.datasetKey, entity.id)}
                            className="shrink-0 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1.5 mt-4 text-xs font-semibold text-cyan-100 transition-colors hover:border-cyan-300 hover:text-white"
                          >
                            View
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">
                        No linked entities match.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {linkedEntityGroups.map((group) => (
                  <button
                    key={group.datasetKey}
                    type="button"
                    onClick={() => handleSelectLinkedEntityDataset(group.datasetKey)}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-3 text-left transition-colors hover:border-cyan-400/40 hover:bg-slate-900/60"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-100">
                        {group.datasetLabel}
                      </span>
                      <span className="mt-1 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                        Linked dataset
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full border border-slate-700 bg-slate-950/80 px-2.5 py-1 text-[11px] font-semibold text-slate-200">
                      {group.items.length}
                    </span>
                  </button>
                ))}
              </div>
            )
          ) : (
            <p className="text-sm text-slate-500">No linked entities available.</p>
          )}
        </section>
      </div>
    </>
  );
};

export default CompanyDetailLayout;
